import { useQuery } from "@tanstack/react-query";
import { queryClient } from "app/global";
import { routes } from "pages/routes";
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import getBrowserInfo from "services/browserSupport";
import { bootIntercom } from "services/intercom";
import { logError, logInfo, updateUserLoggingContext } from "services/logging";
import { identifyUser } from "services/productAnalytics";
import { fetchStudentProfile } from "services/student";
import { currentUserQuery, refreshCurrentUser } from "services/users";
import { clearUserSession, logOut } from "services/users/auth";
import { User } from "services/users/model";
import { EnhancedUser, enhanceUser } from "services/users/utils";
import { SessionStorageKeys } from "utils/storage";

export type UserContextValue = {
  // undefined = session status unknown, null = no session
  user: User | null | undefined;
  refreshCurrentUser: () => Promise<User>;
  refetchStudent: () => void;
  signOut: () => Promise<void>;
};

export const UserContext = createContext<UserContextValue | undefined>(undefined);
UserContext.displayName = "UserContext";

export function UserContextProvider({ children }: PropsWithChildren) {
  const { data: user } = useQuery(currentUserQuery());

  useEffect(() => {
    if (!user) {
      return;
    }

    sessionStorage.setItem(SessionStorageKeys.user, JSON.stringify(user));
    identifyUser(user);
    updateUserState(user);
    updateUserLoggingContext(user);

    logInfo("signed in, browser: " + getBrowserInfo().additionalBrowserInfo);

    void bootIntercom(user).catch(logError);
    return () => void bootIntercom(null).catch(logError);
  }, [user]);

  const refetchStudent = useCallback(async () => {
    const student = await fetchStudentProfile();
    updateUserState(usr => ({
      ...usr,
      student,
    }));
  }, []);

  const navigate = useNavigate();
  const signOut = useCallback(async () => {
    try {
      const resp = await logOut();
      if (resp?.logout_url) {
        window.location.replace(resp.logout_url);
      } else {
        navigate(routes.public.signIn.path());
      }
    } finally {
      clearUserSession();
    }
  }, [navigate]);

  const contextValue = useMemo(
    () => ({
      user,
      refreshCurrentUser,
      refetchStudent,
      signOut,
    }),
    [user, refetchStudent, signOut]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

export function useUser(): EnhancedUser;
export function useUser(required: true): EnhancedUser;
export function useUser(required: false): EnhancedUser | null | undefined;
export function useUser(required: boolean): EnhancedUser | null | undefined;
export function useUser(required = true) {
  const { data: user } = useQuery(currentUserQuery());

  if (required && !user) {
    throw new Error("Required user data is not available.");
  }

  return useMemo(() => (user ? enhanceUser(user) : user), [user]);
}

export function updateUserState(newData: User | ((currentUser: User) => User)) {
  const existingUser = queryClient.getQueryData(currentUserQuery().queryKey);
  if (!existingUser) {
    throw new Error("No user data available to update.");
  }
  const updatedUserData = typeof newData === "function" ? newData(existingUser) : newData;
  queryClient.setQueryData(currentUserQuery().queryKey, updatedUserData);
  sessionStorage.setItem(SessionStorageKeys.user, JSON.stringify(updatedUserData));
}
